import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import { Home, Info, Search, Globe, Menu, X } from './Icons';
import logo from '../assets/logo.png';
import styles from './PublicNavbar.module.css';

function PublicNavbar() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const drawerRef = useRef(null);

  const isID = i18n.language === 'id';
  const isActive = (path) => location.pathname === path;

  const links = [
    { to: '/landing', label: isID ? 'Beranda' : 'Home', icon: <Home size={18} aria-hidden="true" /> },
    { to: '/public-info', label: isID ? 'Informasi' : 'Information', icon: <Info size={18} aria-hidden="true" /> },
    { to: '/public-screening', label: isID ? 'Tes Skrining' : 'Screening', icon: <Search size={18} aria-hidden="true" /> },
  ];

  // Tutup drawer: Escape + kunci scroll body + fokus ke drawer
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Tutup drawer otomatis saat pindah halaman
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <nav className={styles.nav}>
      <div className={styles.inner}>
        {/* Logo */}
        <button className={styles.logo} onClick={() => navigate('/landing')}
          aria-label={isID ? 'Ke Beranda NOWGUT' : 'Go to NOWGUT Home'}>
          <img src={logo} alt="" />
          <span className={styles.logoText}>
            <span className={styles.logoName}>NOWGUT</span>
            <span className={styles.logoTagline}>
              {isID ? 'Pemantau Risiko Saluran Cerna Anak' : 'Child GI Risk Monitor'}
            </span>
          </span>
        </button>

        {/* Right */}
        <div className={styles.right}>
          <button onClick={() => i18n.changeLanguage(isID ? 'en' : 'id')}
            className={styles.langBtn}
            aria-label={isID ? 'Ubah ke Bahasa Inggris' : 'Switch to Indonesian'}>
            <Globe size={18} aria-hidden="true" />
            {isID ? 'EN' : 'ID'}
          </button>

          {!isMobile && (
            <>
              {links.map(l => (
                <Link key={l.to} to={l.to}
                  className={`${styles.link} ${isActive(l.to) ? styles.linkActive : ''}`}
                  aria-current={isActive(l.to) ? 'page' : undefined}>
                  {l.icon}{l.label}
                </Link>
              ))}
              <Link to="/login" className={styles.loginBtn}>{isID ? 'Masuk' : 'Login'}</Link>
              <Link to="/register" className={styles.registerBtn}>{isID ? 'Daftar' : 'Sign Up'}</Link>
            </>
          )}

          {isMobile && (
            <button className={styles.hamburger}
              onClick={() => setMenuOpen(true)}
              aria-label={isID ? 'Buka menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-drawer">
              <Menu size={22} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {isMobile && menuOpen && (
        <>
          <div className={styles.overlay} onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div id="mobile-drawer" ref={drawerRef} tabIndex={-1}
            className={styles.drawer} role="dialog" aria-modal="true"
            aria-label={isID ? 'Menu navigasi' : 'Navigation menu'}>
            <div className={styles.drawerHeader}>
              <button className={styles.drawerClose} onClick={() => setMenuOpen(false)}
                aria-label={isID ? 'Tutup menu' : 'Close menu'}>
                <X size={22} aria-hidden="true" />
              </button>
            </div>

            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`${styles.mobileLink} ${isActive(l.to) ? styles.mobileLinkActive : ''}`}
                aria-current={isActive(l.to) ? 'page' : undefined}
                onClick={() => setMenuOpen(false)}>
                {l.icon}{l.label}
              </Link>
            ))}

            <div className={styles.mobileAuth}>
              <Link to="/login" className={styles.mobileLogin} onClick={() => setMenuOpen(false)}>
                {isID ? 'Masuk' : 'Login'}
              </Link>
              <Link to="/register" className={styles.mobileRegister} onClick={() => setMenuOpen(false)}>
                {isID ? 'Daftar Gratis' : 'Sign Up Free'}
              </Link>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

export default PublicNavbar;